import {useState, ChangeEvent, useEffect, useRef} from 'react';
import {TEXT_INPUT_STYLE} from './styles';
import {PakConfiguration} from './pineridge-channel';

interface PakConfigInputsProps {
  isOpen: boolean;
  initialConfig: PakConfiguration;
  updateConfig: (config: PakConfiguration) => void;
  updateConfigError: (hasError: boolean) => void;
  disabled: boolean;
}

export function PakConfigInputs({
  isOpen,
  initialConfig,
  updateConfig,
  updateConfigError,
  disabled,
}: PakConfigInputsProps) {
  const initialUserId = initialConfig ? initialConfig.userId : '';
  const initialUserIdError = !initialUserId ? 'Required' : '';
  const initialChannelId = initialConfig ? initialConfig.channelId : '';
  const initialChannelIdError = !initialChannelId ? 'Required' : '';
  const initialAllowPublishAudio = initialConfig ? initialConfig.allowPublishAudio : true;
  const initialAllowPublishVideo = initialConfig ? initialConfig.allowPublishVideo : true;
  const initialAllowPublishScreenShare = initialConfig ? initialConfig.allowPublishScreenShare : true;
  const initialAllowBotChat = initialConfig ? initialConfig.allowBotChat : true;
  const initialAllowVoiceDataCollection = initialConfig ? initialConfig.allowVoiceDataCollection : true;
  const initialTokenLifetimeSec = initialConfig?.tokenLifetimeSec ? String(initialConfig.tokenLifetimeSec) : '';
  const initialTokenLifetimeSecError =
    initialTokenLifetimeSec && isNaN(parseInt(initialTokenLifetimeSec)) ? 'Invalid' : '';

  const [userId, setUserId] = useState(initialUserId);
  const [userIdError, setUserIdError] = useState(initialUserIdError);
  const [channelId, setChannelId] = useState(initialChannelId);
  const [channelIdError, setChannelIdError] = useState(initialChannelIdError);
  const [allowPublishAudio, setAllowPublishAudio] = useState(initialAllowPublishAudio);
  const [allowPublishVideo, setAllowPublishVideo] = useState(initialAllowPublishVideo);
  const [allowPublishScreenShare, setAllowPublishScreenShare] = useState(initialAllowPublishScreenShare);
  const [allowBotChat, setAllowBotChat] = useState(initialAllowBotChat);
  const [allowVoiceDataCollection, setAllowVoiceDataCollection] = useState(initialAllowVoiceDataCollection);
  const [tokenLifetimeSec, setTokenLifetimeSec] = useState(initialTokenLifetimeSec);
  const [tokenLifetimeSecError, setTokenLifetimeSecError] = useState(initialTokenLifetimeSecError);
  const channelIdRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && channelIdRef.current) {
      channelIdRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    updateConfig({
      pak: initialConfig.pak,
      userId,
      channelId,
      allowPublishAudio,
      allowPublishVideo,
      allowPublishScreenShare,
      allowBotChat,
      allowVoiceDataCollection,
      tokenLifetimeSec: tokenLifetimeSec ? parseInt(tokenLifetimeSec) : undefined,
    });
  }, [
    userId,
    channelId,
    allowPublishAudio,
    allowPublishVideo,
    allowPublishScreenShare,
    allowBotChat,
    allowVoiceDataCollection,
    tokenLifetimeSec,
  ]);

  useEffect(() => {
    updateConfigError(!!userIdError || !!channelIdError || !!tokenLifetimeSecError);
  }, [userIdError, channelIdError, tokenLifetimeSecError]);

  function handleChangeUserId(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setUserId(value);
    if (!value) {
      setUserIdError('Required');
    } else {
      setUserIdError('');
    }
  }

  function handleChangeChannelId(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setChannelId(value);
    if (!value) {
      setChannelIdError('Required');
    } else {
      setChannelIdError('');
    }
  }

  function handleChangeTokenLifetimeSec(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setTokenLifetimeSec(value);
    if (value && isNaN(parseInt(value))) {
      setTokenLifetimeSecError('Invalid');
    } else {
      setTokenLifetimeSecError('');
    }
  }

  return (
    <div className="mt-2 text-sm grid grid-cols-3">
      <div className="mr-1 mb-1 font-bold">User ID</div>
      <div className="mr-1 mb-1">
        <input className={TEXT_INPUT_STYLE} value={userId} onChange={handleChangeUserId} disabled={disabled} />
      </div>
      <div className="mb-1 text-red-500">{userIdError}</div>

      <div className="mr-1 mb-1 font-bold">Channel ID</div>
      <div className="mr-1 mb-1">
        <input
          className={TEXT_INPUT_STYLE}
          value={channelId}
          ref={channelIdRef}
          onChange={handleChangeChannelId}
          disabled={disabled}
        />
      </div>
      <div className="mb-1 text-red-500">{channelIdError}</div>

      <div className="mr-1 mb-1 font-bold">Allow Publish Audio</div>
      <div className="mr-1 mb-1">
        <input
          type="checkbox"
          checked={allowPublishAudio}
          onChange={e => setAllowPublishAudio(e.target.checked)}
          disabled={disabled}
        />
      </div>
      <div>{/* This is just here to fill out the grid row */}</div>

      <div className="mr-1 mb-1 font-bold">Allow Publish Video</div>
      <div className="mr-1 mb-1">
        <input
          type="checkbox"
          checked={allowPublishVideo}
          onChange={e => setAllowPublishVideo(e.target.checked)}
          disabled={disabled}
        />
      </div>
      <div>{/* This is just here to fill out the grid row */}</div>

      <div className="mr-1 mb-1 font-bold">Allow Publish Screenshare</div>
      <div className="mr-1 mb-1">
        <input
          type="checkbox"
          checked={allowPublishScreenShare}
          onChange={e => setAllowPublishScreenShare(e.target.checked)}
          disabled={disabled}
        />
      </div>
      <div>{/* This is just here to fill out the grid row */}</div>

      <div className="mr-1 mb-1 font-bold">Allow Bot Chat</div>
      <div className="mr-1 mb-1">
        <input
          type="checkbox"
          checked={allowBotChat}
          onChange={e => setAllowBotChat(e.target.checked)}
          disabled={disabled}
        />
      </div>
      <div>{/* This is just here to fill out the grid row */}</div>

      <div className="mr-1 mb-1 font-bold">Allow Voice Data Collection</div>
      <div className="mr-1 mb-1">
        <input
          type="checkbox"
          checked={allowVoiceDataCollection}
          onChange={e => setAllowVoiceDataCollection(e.target.checked)}
          disabled={disabled}
        />
      </div>
      <div>{/* This is just here to fill out the grid row */}</div>

      <div className="mr-1 mb-1 font-bold">SAT Lifetime (s)</div>
      <div className="mr-1 mb-1">
        <input
          className={TEXT_INPUT_STYLE}
          value={tokenLifetimeSec}
          onChange={handleChangeTokenLifetimeSec}
          placeholder="86400"
          disabled={disabled}
        />
      </div>
      <div className="mb-1 text-red-500">{tokenLifetimeSecError}</div>
    </div>
  );
}
